import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Grid,
} from "@mui/material";

const QuotationPrintTemplate = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();

  const { data = {}, tableData = [], navigateBack } = location.state || {};
  const decimalCount = useSelector((state) => state.common?.decimalCount) || 2;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(navigateBack || -1);
    },
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handlePrint();
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const themeColor = "#0B3D55"; // Dark purple matching the image

  const getImageUrl = (prod) => {
    let rawImg = prod.photo || prod.image || prod.image_url || "";
    if (!rawImg) return "";

    try {
      // If it's a JSON string, parse it to extract the 'img' property
      if (typeof rawImg === "string" && rawImg.trim().startsWith("{")) {
        const parsed = JSON.parse(rawImg);
        return parsed.img || rawImg;
      }
    } catch (error) {
      console.log("Error parsing image JSON:", error);
    }

    return rawImg;
  };

  const borderStyle = "1px solid black";

  return (
    <Box sx={{ display: "none" }}>
      <Box
        ref={componentRef}
        sx={{
          boxSizing: "border-box",
          p: "20px",
          fontFamily: "'Roboto', sans-serif",
          color: "black",
          width: "100%",
          maxWidth: "210mm",
          minHeight: "270mm",
          display: "flex",
          flexDirection: "column",
          m: "0 auto",
          bgcolor: "white",
        }}
      >
        {/* Header Section */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography
            sx={{
              fontSize: "36px",
              fontWeight: "900",
              color: themeColor,
              m: 0,
              textTransform: "uppercase",
            }}
          >
            Quotation
          </Typography>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Box sx={{ fontWeight: "bold", fontSize: "15px" }}>
              <Box component="span" sx={{ mr: "10px" }}>
                Order No:
              </Box>
              <Box component="span">{data.BillNo || ""}</Box>
            </Box>
            <Box sx={{ width: "150px" }}>
              <Table
                size="small"
                sx={{
                  border: borderStyle,
                  "& .MuiTableCell-root": {
                    border: borderStyle,
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "14px",
                    p: "4px",
                    color: "black",
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell>DATE</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {data.BillDate
                        ? dayjs(data.BillDate).format("DD-MM-YY")
                        : ""}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </Stack>

        {/* Customer Info */}
        <Box
          sx={{
            bgcolor: themeColor,
            color: "white",
            p: "4px 8px",
            fontWeight: "bold",
            fontSize: "14px",
            mt: "10px",
            width: "100%",
          }}
        >
          CUSTOMER INFO
        </Box>

        <Grid
          container
          justifyContent="space-between"
          sx={{ mt: "10px", fontSize: "15px" }}
        >
          <Grid item>
            <Box sx={{ fontWeight: "bold", mb: "4px" }}>
              Name: {data.BillPartyName || ""}
            </Box>
            <Box sx={{ fontWeight: "bold", mb: "4px" }}>
              Address: {data.warranty || ""}
            </Box>
            <Box sx={{ fontWeight: "bold" }}>
              Tax Details: {data.vat_no || ""}
            </Box>
          </Grid>
          <Grid item sx={{ textAlign: "right" }}>
            <Box sx={{ fontWeight: "bold" }}>
              <Box component="span" sx={{ mr: "15px" }}>
                PH:
              </Box>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  minWidth: "120px",
                  textAlign: "left",
                }}
              >
                {data.Ph || ""}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Items Table */}
        <TableContainer
          sx={{ mt: "15px", border: borderStyle, overflow: "visible" }}
        >
          <Table
            sx={{
              "& .MuiTableCell-root": {
                border: borderStyle,
                p: "8px 5px",
                fontSize: "13px",
                color: "black",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  { label: "Sl no", width: "5%" },
                  { label: "Model No", width: "15%" },
                  { label: "PHOTO", width: "20%" },
                  { label: "Discription", width: "25%" },
                  { label: "Qty", width: "8%" },
                  { label: "Unit Price", width: "12%" },
                  { label: "Amount", width: "15%" },
                ].map((col, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      bgcolor: themeColor,
                      color: "white !important",
                      fontWeight: "bold",
                      textAlign: "center",
                      width: col.width,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((prod, index) => {
                const photoUrl = getImageUrl(prod);
                return (
                  <TableRow key={index}>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell align="center">{prod.Size || "NA"}</TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          width: "120px",
                          height: "80px",
                          m: "0 auto",
                          bgcolor: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt="Product"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <Typography sx={{ color: "#ccc", fontSize: "12px" }}>
                            No Image
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "left",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      {prod.ItemNameTextField}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {prod.qty}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {(Number(prod.Rate1) || 0).toFixed(decimalCount)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {(Number(prod.total) || 0).toFixed(decimalCount)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ flexGrow: 1 }} />

        {/* Footer Summary */}
        <Stack
          direction="row"
          alignItems="stretch"
          sx={{ mt: "auto", width: "100%" }}
        >
          {/* Left side: Balance and Terms */}
          <Box sx={{ width: "60%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ width: "100%" }}>
              <Table
                size="small"
                sx={{
                  border: borderStyle,
                  borderTop: "none",
                  "& .MuiTableCell-root": {
                    border: "1px solid white",
                    bgcolor: "#f0f0f0",
                    color: "black",
                    p: "8px",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: "14px",
                    width: "50%",
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell>BALANCE</TableCell>
                    <TableCell>
                      {Number(
                        data.BalanceAmount !== undefined &&
                          data.BalanceAmount !== null
                          ? data.BalanceAmount
                          : data.BillNetTotalField || 0,
                      ).toFixed(decimalCount)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
            {localStorage.getItem("termsCondition") && (
              <Box
                sx={{
                  border: borderStyle,
                  borderTop: "none",
                  p: "4px",
                  fontSize: "12px",
                  "& p": { margin: 0 },
                  flexGrow: 1,
                }}
                dangerouslySetInnerHTML={{
                  __html: localStorage.getItem("termsCondition"),
                }}
              />
            )}
          </Box>

          {/* Right side: Totals */}
          <Box sx={{ width: "40%", ml: "-1px" }}>
            <Table
              size="small"
              sx={{
                border: borderStyle,
                borderTop: "none",
                height: "100%",
                "& .MuiTableCell-root": {
                  border: "1px solid white",
                  bgcolor: themeColor,
                  color: "white",
                  p: "8px",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: "14px",
                  width: "50%",
                },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell sx={{ borderTop: "2px solid white !important" }}>
                    SUB TOTAL
                  </TableCell>
                  <TableCell sx={{ borderTop: "2px solid white !important" }}>
                    {Number(data.BillTotalField || 0).toFixed(decimalCount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ borderTop: "2px solid white !important" }}>
                    NET TOTAL
                  </TableCell>
                  <TableCell sx={{ borderTop: "2px solid white !important" }}>
                    {Number(data.BillNetTotalField || 0).toFixed(decimalCount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default QuotationPrintTemplate;
